import { getTimezoneOffsetInfo, getBrowserTimezone } from '../utils/timezoneOptions'

interface TimezoneIndicatorProps {
  entryTimezone?: string
  showIndicator?: boolean // From user settings
  colorClass?: string // Custom color class (defaults to text-gray-500)
  /** Optional reference date for DST-accurate offset calculation */
  referenceDate?: Date
}

// Default timezone for legacy entries that don't have timezone stored
const DEFAULT_TIMEZONE = 'Europe/Berlin'

/**
 * Displays a clock icon with directional arrow when an entry's timezone differs from the browser's timezone.
 * - Counter-clockwise arrow for timezones behind browser time
 * - Clockwise arrow for timezones ahead of browser time
 * - Simple clock (no arrow) for different timezone but same time offset (gray color)
 * On hover, shows a tooltip with the original timezone and time difference.
 * Legacy entries without a stored timezone are assumed to be in Europe/Berlin.
 */
export default function TimezoneIndicator({
  entryTimezone,
  showIndicator = true,
  colorClass = 'text-gray-500',
  referenceDate
}: TimezoneIndicatorProps) {
  // Get current browser timezone
  const browserTimezone = getBrowserTimezone()

  // Use default timezone for legacy entries without timezone
  const effectiveTimezone = entryTimezone || DEFAULT_TIMEZONE

  // Don't show anything if:
  // - User has disabled the indicator
  // - Effective timezone matches browser timezone
  if (!showIndicator || effectiveTimezone === browserTimezone) {
    return null
  }

  // Get timezone offset info for direction and formatted difference
  const offsetInfo = getTimezoneOffsetInfo(effectiveTimezone, browserTimezone, referenceDate)

  // Format timezone for display (e.g., "Europe/Berlin" -> "Europe/Berlin")
  const formatTimezone = (tz: string) => {
    // Replace underscores with spaces for better readability
    return tz.replace(/_/g, ' ')
  }

  // Build tooltip with timezone and time difference
  const tooltip = offsetInfo.direction === 'same'
    ? `Recorded in ${formatTimezone(effectiveTimezone)}`
    : `Recorded in ${formatTimezone(effectiveTimezone)} (${offsetInfo.formattedDifference})`

  // Use gray color for same time offset (different timezone but no time difference)
  const effectiveColorClass = offsetInfo.direction === 'same' ? 'text-gray-400' : colorClass

  return (
    <span
      className={`inline-flex items-center justify-center w-4 h-4 ${effectiveColorClass}`}
      title={tooltip}
    >
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        {/* Clock circle */}
        <circle cx="12" cy="12" r="8" />
        {/* Clock hands */}
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 8v4l2.5 2.5"
        />
        {offsetInfo.direction === 'behind' ? (
          // Counter-clockwise arrow (timezone is behind browser = earlier time)
          <>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v4h4" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 8A9 9 0 0112 3" />
          </>
        ) : offsetInfo.direction === 'ahead' ? (
          // Clockwise arrow (timezone is ahead of browser = later time)
          <>
            <path strokeLinecap="round" strokeLinejoin="round" d="M20 4v4h-4" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M20 8A9 9 0 0012 3" />
          </>
        ) : null /* 'same' direction - no arrow, just clock */}
      </svg>
    </span>
  )
}
